// Kolory statusów cyklu życia prezlecenia i zlecenia. Słownik tymczasowy
// (statusy = kwestia otwarta K-05, uzgodnienie 4 działów) — do zmiany po decyzji.
type ChipColor = 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';

// Prezlecenie: NOWE / W_WERYFIKACJI / UZUPELNIANE / ZATWIERDZONE / ODRZUCONE
export const preOrderStatusColor = (s?: string | null): ChipColor => {
  switch (s) {
    case 'ZATWIERDZONE':
      return 'success';
    case 'W_WERYFIKACJI':
      return 'warning';
    case 'UZUPELNIANE':
      return 'info';
    case 'ODRZUCONE':
      return 'error';
    default:
      return 'default';
  }
};

/**
 * Status prezlecenia po polsku. Wartość w bazie zostaje kodem (`W_WERYFIKACJI`)
 * — to dane, nie tekst dla operatora. Nieznany kod pokazujemy, jak przyszedł.
 */
export const preOrderStatusLabel = (s?: string | null): string => {
  switch (s ?? 'NOWE') {
    case 'NOWE':
      return 'nowe';
    case 'W_WERYFIKACJI':
      return 'w weryfikacji';
    case 'UZUPELNIANE':
      return 'uzupełniane';
    case 'ZATWIERDZONE':
      return 'zatwierdzone';
    case 'ODRZUCONE':
      return 'odrzucone';
    default:
      return String(s);
  }
};

// Zapis do VIA (W10): DRAFT → APPROVED → XML_GENERATED → DELIVERED →
// DETECTED_IN_VIA → ACCEPTED_IN_VIA ; FAILED ; CONFIRMED_IN_TMS („Jest w TMS”,
// połączenie z TMS, 16.10.2026)
export const VIA_WRITE_STATUSES = [
  'DRAFT',
  'APPROVED',
  'XML_GENERATED',
  'DELIVERED',
  'DETECTED_IN_VIA',
  'ACCEPTED_IN_VIA',
  'CONFIRMED_IN_TMS',
  'FAILED',
];
/**
 * Stany „zlecenie jest już po stronie VIA/TMS” — nadpisać pliku się nie da,
 * zmiana to „usuń w VIA i wprowadź ponownie”. Lustro `IN_VIA_STATES`
 * z `api/src/via/write-states.ts` (pilnuje test).
 */
export const VIA_WRITE_IN_VIA_STATES: readonly string[] = [
  'DETECTED_IN_VIA',
  'ACCEPTED_IN_VIA',
  'CONFIRMED_IN_TMS',
];
export const viaWriteStatusColor = (s?: string | null): ChipColor => {
  switch (s) {
    case 'ACCEPTED_IN_VIA':
    case 'DETECTED_IN_VIA':
    case 'CONFIRMED_IN_TMS':
      return 'success';
    case 'DELIVERED':
    case 'XML_GENERATED':
      return 'info';
    case 'APPROVED':
      return 'warning';
    case 'FAILED':
      return 'error';
    default:
      return 'default';
  }
};

/**
 * Źródło zlecenia (`Order.source`) — wartość w bazie zostaje kodem.
 * `MANUAL` od 28.09.2026: zlecenie z prezlecenia wpisanego z ręki miało
 * wcześniej `MAIL` i chip „Tor mailowy”, choć maila nie było.
 */
export const ORDER_SOURCES = ['MAIL', 'MANUAL', 'VIA'] as const;
export const orderSourceLabel = (s?: string | null): string => {
  switch (s) {
    case 'MAIL':
      return 'Tor mailowy (W3→W10)';
    case 'MANUAL':
      return 'Wpisane ręcznie';
    case 'VIA':
      return 'Odczyt z VIA (W11)';
    default:
      return s ?? '—';
  }
};

// Rola powiązania mail ↔ prezlecenie (EmailPreOrder).
export const linkRoleLabel = (r?: string | null): string =>
  r === 'SOURCE' ? 'Źródło' : r === 'CHANGE' ? 'Zmiana' : r === 'DUPLICATE_OF' ? 'Duplikat' : (r ?? '');
export const linkRoleColor = (r?: string | null): ChipColor =>
  r === 'CHANGE' ? 'warning' : r === 'DUPLICATE_OF' ? 'error' : 'default';

/** Przełącznik eksportu do TMS jedną akcją i nowych nazw stanów (S-21.10-11). */
export const FEATURE_TMS_EXPORT_ONE_STEP = 'tmsExportOneStep';

/** Lustro `TMS_EXPORT_STATE_LABELS` z `api/src/via/write-states.ts`, wielką literą. */
const TMS_EXPORT_STATE_NAMES: Record<string, string> = {
  XML_GENERATED: 'Wysłane do TMS (plik wytworzony)',
  DELIVERED: 'Wysłane do TMS (dostarczone)',
  CONFIRMED_IN_TMS: 'Zapisane w TMS',
};

/** To samo dla pojedynczego pliku (`ViaWrite.status`). */
const TMS_EXPORT_FILE_NAMES: Record<string, string> = {
  GENERATED: 'Wysłany do TMS (plik wytworzony)',
  DELIVERED: 'Wysłany do TMS (dostarczony)',
  CONFIRMED_IN_TMS: 'Zapisany w TMS',
};

/**
 * Stan zapisu w wersji konta: z przełącznikiem `tmsExportOneStep` (paczka
 * p. Mariusza 21.10, BR-ADM-10; decyzja użytkownika 21.10.2026 pkt 6)
 * „wysłane do TMS” i „zapisane w TMS”, bez niego — `viaWriteStatusLabel`.
 */
export const viaWriteStateName = (s: string | null | undefined, tmsNames: boolean): string =>
  (tmsNames ? TMS_EXPORT_STATE_NAMES[s ?? ''] : undefined) ?? viaWriteStatusLabel(s);

/** To samo dla pojedynczego pliku — bez przełącznika `viaFileStatusLabel`. */
export const viaFileStateName = (s: string | null | undefined, tmsNames: boolean): string =>
  (tmsNames ? TMS_EXPORT_FILE_NAMES[s ?? ''] : undefined) ?? viaFileStatusLabel(s);

/**
 * Stan zapisu zlecenia do VIA (`Order.viaWriteStatus`) po polsku. Kod
 * w bazie zostaje kodem. Te same nazwy, małą literą, wstawia API do odmów
 * (`api/src/via/write-states.ts`, `writeStateLabel`).
 *
 * 07.10.2026 (arkusz „Problemy poza brzmieniem”): brak stanu to szkic, ale
 * kod, którego nie znamy, pokazujemy, jak przyszedł — wcześniej każdy
 * nieznany kod udawał „Wersję roboczą”. „Szkic” zamiast „Wersji roboczej”
 * i „Plik wytworzony” zamiast „XML wygenerowany” — tymi słowami mówią
 * przyciski i komunikaty panelu zapisu („Wytwórz plik”, „wróciło do szkicu”).
 */
export const viaWriteStatusLabel = (s?: string | null): string => {
  switch (s ?? 'DRAFT') {
    case 'ACCEPTED_IN_VIA':
      return 'Zaakceptowane w VIA';
    case 'DETECTED_IN_VIA':
      return 'Wykryte w VIA';
    case 'CONFIRMED_IN_TMS':
      // „Jest w TMS”, nie „Przyjęte” (decyzja użytkownika 16.10.2026): my do
      // TMS nic nie zapisujemy — zlecenie z naszym numerem widać w imporcie
      // z TMS, bez względu na to, kto je tam wprowadził.
      return 'Jest w TMS';
    case 'DELIVERED':
      return 'Dostarczone';
    case 'XML_GENERATED':
      return 'Plik wytworzony';
    case 'APPROVED':
      return 'Zatwierdzone do zapisu';
    case 'FAILED':
      return 'Błąd zapisu';
    case 'DRAFT':
    case '':
      return 'Szkic';
    default:
      return String(s);
  }
};

/**
 * Status jednego pliku zapisu (`ViaWrite.status`: GENERATED / DELIVERED /
 * DETECTED / ACCEPTED / FAILED) — na karcie zlecenia szedł na ekran kodem.
 * Kod w bazie zostaje; nieznany pokazujemy, jak przyszedł.
 */
export const viaFileStatusLabel = (s?: string | null): string => {
  switch (s) {
    case 'GENERATED':
      return 'Plik wytworzony';
    case 'DELIVERED':
      return 'Dostarczony';
    case 'DETECTED':
      return 'Wykryty w VIA';
    case 'ACCEPTED':
      return 'Zaakceptowany w VIA';
    case 'CONFIRMED_IN_TMS':
      return 'Jest w TMS';
    case 'FAILED':
      return 'Błąd zapisu';
    default:
      return s ?? '—';
  }
};
export const viaFileStatusColor = (s?: string | null): ChipColor =>
  s === 'ACCEPTED' || s === 'DETECTED' || s === 'CONFIRMED_IN_TMS'
    ? 'success'
    : s === 'FAILED'
      ? 'error'
      : s === 'DELIVERED'
        ? 'info'
        : 'default';

/**
 * Rodzaj komunikatu z VIA (`ViaOrder.messageType`, z kwalifikatora pliku
 * tisys): `assigned` (tour.assign) — zlecenie przydzielone przewoźnikowi,
 * `accepted` — przyjęte, `modified` (tour.assigned.change) — pełny obraz
 * zlecenia po zmianie; `unknown` — parser nie rozpoznał. Kod zostaje
 * w bazie i w filtrze listy; na ekranie słowo.
 */
export const VIA_MESSAGE_TYPES = ['assigned', 'accepted', 'modified'] as const;
export const viaMessageTypeLabel = (t?: string | null): string => {
  switch (t) {
    case 'assigned':
      return 'Przydzielenie zlecenia';
    case 'accepted':
      return 'Przyjęcie zlecenia';
    case 'modified':
      return 'Zmiana zlecenia';
    case 'unknown':
      return 'Nierozpoznany';
    default:
      return t ?? '—';
  }
};

/**
 * Klasyfikacja maila (`Email.classification`, enum `MessageClass`)
 * słowem; kod (`ORDER`) zostaje w bazie i w filtrze skrzynki. Wątek
 * wiadomości pokazywał kod (07.10.2026). Nieznany — jak przyszedł.
 */
const CLASSIFICATION_LABEL: Record<string, string> = {
  ORDER: 'Zlecenie',
  INQUIRY: 'Zapytanie',
  CHANGE: 'Zmiana',
  COMPLAINT: 'Reklamacja',
  INTERNAL: 'Wewnętrzna',
  OTHER: 'Inne',
};
export const classificationLabel = (c?: string | null): string =>
  c ? (CLASSIFICATION_LABEL[c] ?? c) : '—';

/**
 * Stan odczytu prezlecenia z maila (`PreOrder.extractionStatus`: PENDING /
 * EXTRACTED / NEEDS_REVIEW). Wcześniej osobna mapa na karcie banku, a karta
 * prezlecenia pokazywała kod — jedna funkcja dla obu (07.10.2026).
 */
export const extractionStatusLabel = (s?: string | null): string => {
  switch (s) {
    case 'PENDING':
      return 'oczekuje';
    case 'EXTRACTED':
      return 'odczytane';
    case 'NEEDS_REVIEW':
      return 'do sprawdzenia';
    default:
      return s ?? '—';
  }
};
